import { useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { btnPrimary, btnSecondary, inputCls } from "@/components/admin/ui";

const Choice = ({ title, desc, onClick, testId }) => (
  <button type="button" onClick={onClick} className="flex-1 text-left rounded-lg border border-[#E5E7EB] p-5 hover:border-[#111] hover:bg-[#F9FAFB] transition-colors" data-testid={testId}>
    <p className="text-[15px] font-semibold">{title}</p>
    <p className="mt-1 text-[12.5px] text-[#6B7280]">{desc}</p>
  </button>
);

export const AddVideoDialog = ({ open, onOpenChange, onAdded, addFiles, jobs }) => {
  const [mode, setMode] = useState("choose");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setMode("choose"); setUrl(""); }, 200);
  };

  const addYoutube = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/videos/youtube", { url });
      toast.success(`Added “${data.title}”`);
      onAdded?.();
      close(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onFiles = (files) => {
    const n = addFiles(files);
    if (!n) toast.error("No video files found");
    else toast.success(`${n} video${n > 1 ? "s" : ""} added to the upload queue`);
  };

  const activeJobs = jobs.filter((j) => j.state !== "error");

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[560px] font-sans" data-testid="add-video-dialog">
        <DialogHeader>
          <DialogTitle className="text-[18px]">Add Video</DialogTitle>
          <DialogDescription>{mode === "choose" ? "Is this a YouTube video to embed, or a video file to upload?" : mode === "youtube" ? "Paste the YouTube link below." : "Drag & drop video files. Two upload at a time; the rest wait in the queue."}</DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Choice title="YouTube video" desc="Embed a video from a YouTube link" onClick={() => setMode("youtube")} testId="add-video-choice-youtube" />
            <Choice title="Upload video" desc="Upload video files from your computer" onClick={() => setMode("upload")} testId="add-video-choice-upload" />
          </div>
        )}

        {mode === "youtube" && (
          <form onSubmit={addYoutube} className="space-y-4">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className={inputCls} autoFocus data-testid="youtube-url-input" />
            <div className="flex justify-between">
              <button type="button" className={btnSecondary} onClick={() => setMode("choose")} data-testid="add-video-back">Back</button>
              <button type="submit" className={btnPrimary} disabled={!url.trim() || busy} data-testid="youtube-add-submit">{busy ? "Adding…" : "Add video"}</button>
            </div>
          </form>
        )}

        {mode === "upload" && (
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              className={`rounded-lg border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragging ? "border-[#B644DC] bg-[#FAF5FF]" : "border-[#D1D5DB] hover:border-[#111]"}`}
              data-testid="upload-dropzone"
            >
              <p className="text-[15px] font-semibold">Drop video files here</p>
              <p className="mt-1 text-[12.5px] text-[#6B7280]">or click to browse · multiple files supported</p>
              <input ref={fileRef} type="file" accept="video/*" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
            </div>
            {activeJobs.length > 0 && (
              <p className="text-[12.5px] text-[#6B7280]" data-testid="upload-dialog-status">
                {activeJobs.length} upload{activeJobs.length > 1 ? "s" : ""} in progress. You can close this window; uploads continue on the Videos page.
              </p>
            )}
            <div className="flex justify-between">
              <button type="button" className={btnSecondary} onClick={() => setMode("choose")} data-testid="add-video-back">Back</button>
              <button type="button" className={btnPrimary} onClick={() => close(false)} data-testid="upload-done">Done</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
